import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, ArrowRight, BookOpen, Loader2, Plus, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  CONFIG_B_OPEN,
  CONFIG_TITLES,
  COST_MODE_OPTIONS,
  DIRECTION_OF_PAYMENT_TEACHING,
  HOLDER_OPTIONS,
  LIVES_THERE_OPTIONS,
  SEQUENCING_TEACHING,
  configNeedsResidentialLease,
  resolveConfig,
  todayIso,
  type CommonAreaUse,
  type CostMode,
  type HolderAnswer,
  type HousingWorkflowData,
  type LivesThereAnswer,
  type RoomPortion,
  type UtilityLine,
} from './housingContent';

interface HousingPhase1Props {
  task: any;
  phaseData?: any;
  onPhaseComplete: () => void;
}

const STEP_TITLES = ['Start here', 'Two questions', 'The place and the people', 'The arrangement', 'Money and records', 'Review'];

function rid(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function HousingPhase1({ task, onPhaseComplete }: HousingPhase1Props) {
  const { toast } = useToast();
  const existing: HousingWorkflowData = task?.workflow_data?.housing || {};

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [unaName, setUnaName] = useState('');

  const [holder, setHolder] = useState<HolderAnswer | undefined>(existing.holder);
  const [livesThere, setLivesThere] = useState<LivesThereAnswer | undefined>(existing.lives_there);

  const [propertyAddress, setPropertyAddress] = useState(existing.property_address || '');
  const [ownerName, setOwnerName] = useState(existing.owner_name || '');
  const [entityLegalName, setEntityLegalName] = useState(existing.entity_legal_name || '');
  const [entitySignerName, setEntitySignerName] = useState(existing.entity_signer_name || '');
  const [entitySignerCapacity, setEntitySignerCapacity] = useState(existing.entity_signer_capacity || '');
  const [officerName, setOfficerName] = useState(existing.association_officer_name || '');
  const [landlordName, setLandlordName] = useState(existing.landlord_name || '');

  const [portions, setPortions] = useState<RoomPortion[]>(
    existing.portions?.length ? existing.portions : [{ id: rid('p'), name: '', square_feet: '', use: '' }],
  );
  const [commonUses, setCommonUses] = useState<CommonAreaUse[]>(
    existing.common_uses?.length ? existing.common_uses : [{ id: rid('c'), area: '', schedule: '' }],
  );
  const [termMonths, setTermMonths] = useState(existing.term_months || '12');
  const [startDate, setStartDate] = useState(existing.start_date || todayIso());
  const [portionRent, setPortionRent] = useState(existing.portion_rent || '');

  const [frvWhole, setFrvWhole] = useState(existing.frv_whole_property || '');
  const [frvBasis, setFrvBasis] = useState(existing.frv_basis || '');
  const [frvSources, setFrvSources] = useState(existing.frv_sources || '');
  const [frvPortionMethod, setFrvPortionMethod] = useState(existing.frv_portion_method || '');

  const [utilities, setUtilities] = useState<UtilityLine[]>(
    existing.utilities?.length ? existing.utilities : [{ id: rid('u'), item: '', who_pays: '', method: '' }],
  );
  const [costMode, setCostMode] = useState<CostMode>(existing.cost_mode === 'itemized' ? 'itemized' : 'inclusive');
  const [hasMortgage, setHasMortgage] = useState<boolean>(!!existing.has_mortgage);
  const [entityResponsibilities, setEntityResponsibilities] = useState(existing.entity_responsibilities || '');
  const [directionOfPayment, setDirectionOfPayment] = useState(!!existing.direction_of_payment);
  const [lenderName, setLenderName] = useState(existing.lender_name || '');
  const [mortgageLender, setMortgageLender] = useState(existing.mortgage_lender || '');
  const [mortgageNotes, setMortgageNotes] = useState(existing.mortgage_notes || '');

  const [residentialRent, setResidentialRent] = useState(existing.residential_rent || '');
  const [residentialPortion, setResidentialPortion] = useState(existing.residential_portion || '');
  const [selfServingTest, setSelfServingTest] = useState(existing.self_serving_test || '');

  const [gatheringCadence, setGatheringCadence] = useState(existing.gathering_cadence || '');
  const [designationNotes, setDesignationNotes] = useState(existing.designation_notes || '');

  const config = resolveConfig(holder, livesThere);

  useEffect(() => {
    async function loadUna() {
      if (!task?.una_id) return;
      const { data } = await supabase
        .from('unas')
        .select('name, primary_officer_role, treasury_secretary_id')
        .eq('id', task.una_id)
        .maybeSingle();
      if (data) {
        setUnaName(data.name || '');
        if (!officerName && data.treasury_secretary_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', data.treasury_secretary_id)
            .maybeSingle();
          if (profile?.full_name) setOfficerName(profile.full_name);
        }
      }
    }
    loadUna();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.una_id]);

  const data: HousingWorkflowData = useMemo(
    () => ({
      holder,
      lives_there: livesThere,
      config: config || undefined,
      property_address: propertyAddress,
      owner_name: ownerName,
      entity_legal_name: entityLegalName,
      entity_signer_name: entitySignerName,
      entity_signer_capacity: entitySignerCapacity,
      association_officer_name: officerName,
      landlord_name: landlordName,
      portions: portions.filter((p) => p.name.trim()),
      common_uses: commonUses.filter((c) => c.area.trim()),
      term_months: termMonths,
      start_date: startDate,
      portion_rent: portionRent,
      frv_whole_property: frvWhole,
      frv_basis: frvBasis,
      frv_sources: frvSources,
      frv_portion_method: frvPortionMethod,
      cost_mode: costMode,
      has_mortgage: hasMortgage,
      utilities: costMode === 'itemized' ? utilities.filter((u) => u.item.trim()) : [],
      entity_responsibilities: entityResponsibilities,
      direction_of_payment: directionOfPayment,
      lender_name: lenderName,
      mortgage_lender: mortgageLender,
      mortgage_notes: mortgageNotes,
      residential_rent: residentialRent,
      residential_portion: residentialPortion,
      self_serving_test: selfServingTest,
      gathering_cadence: gatheringCadence,
      designation_notes: designationNotes,
    }),
    [
      holder,
      livesThere,
      config,
      propertyAddress,
      ownerName,
      entityLegalName,
      entitySignerName,
      entitySignerCapacity,
      officerName,
      landlordName,
      portions,
      commonUses,
      termMonths,
      startDate,
      portionRent,
      frvWhole,
      frvBasis,
      frvSources,
      frvPortionMethod,
      costMode,
      hasMortgage,
      utilities,
      entityResponsibilities,
      directionOfPayment,
      lenderName,
      mortgageLender,
      mortgageNotes,
      residentialRent,
      residentialPortion,
      selfServingTest,
      gatheringCadence,
      designationNotes,
    ],
  );

  const blockedByConfigB = config === 'B' && !CONFIG_B_OPEN;

  async function persist() {
    setSaving(true);
    try {
      const nextData = { ...(task.workflow_data || {}), housing: data };
      await supabase.from('task_workflows').update({ workflow_data: nextData }).eq('task_id', task.id);
      if (task.workflow_data) task.workflow_data.housing = data;
    } catch (e) {
      console.warn('Housing save non fatal error', e);
    } finally {
      setSaving(false);
    }
  }

  function stepIsValid(current: number): string | null {
    if (current === 2) {
      if (!holder || !livesThere) return 'Answer both questions.';
      if (blockedByConfigB) return 'This one is not open yet. Talk with the team before you go further.';
    }
    if (current === 3) {
      if (!propertyAddress.trim()) return 'Enter the address of the place.';
      if (config === 'A' && !ownerName.trim()) return 'Enter the name on the deed.';
      if (!officerName.trim()) return 'Enter the name of the officer who signs for the organization.';
      if (config !== 'A' && !entityLegalName.trim()) return 'Enter the exact legal name of the entity.';
      if (config === 'A' && !entityLegalName.trim()) return 'Enter the exact legal name of the entity that is renting.';
    }
    if (current === 4) {
      if (!gatheringCadence.trim()) return 'Say how often the place is used for gatherings.';
      if (config === 'A' && !portions.some((p) => p.name.trim())) return 'Name at least one room that is leased.';
      if (configNeedsResidentialLease(data) && !residentialPortion.trim())
        return 'Describe the part of the property the household lives in.';
    }
    if (current === 5) {
      if (config === 'A') {
        if (!portionRent.trim()) return 'Enter the monthly rent for the leased portion.';
        if (!frvBasis.trim()) return 'Say where the fair rental value number came from.';
        if (directionOfPayment && !lenderName.trim()) return 'Enter the lender the rent is sent to.';
      }
      if (configNeedsResidentialLease(data)) {
        if (!residentialRent.trim()) return 'Enter the monthly rent the household pays.';
        if (!selfServingTest.trim()) return 'Write the sentence about how this reads to someone outside.';
      }
    }
    return null;
  }

  async function goNext() {
    const problem = stepIsValid(step);
    if (problem) {
      toast({ title: 'One thing first', description: problem, variant: 'destructive' });
      return;
    }
    await persist();
    setStep((s) => Math.min(6, s + 1));
  }

  async function finish() {
    await persist();
    toast({ title: 'Saved', description: 'Your answers are saved. Next you prepare the papers.' });
    onPhaseComplete();
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-base">
              Step {step} of 6: {STEP_TITLES[step - 1]}
            </CardTitle>
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 1 && (
            <div className="space-y-4 text-sm leading-relaxed">
              <div>
                <h4 className="font-medium mb-1">What you need to know</h4>
                <p>
                  The place is really used for the purposes of the organization, and the paper says what actually
                  happens. If the paper and the practice do not match, fix the practice, not the paper. The entity only
                  ever pays bills that carry its own name.
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-1">What you need to do</h4>
                <p>
                  Answer the questions on the next screens. The portal prepares every paper your answers call for, and
                  only those. You sign before anyone moves in and before any money moves, with today's true date.
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-1">Your schedule</h4>
                <p>
                  After setup this asks two things of you: one tap a month for the log, and one sitting a year, about
                  twenty minutes. The portal reminds you and keeps track of everything.
                </p>
              </div>
              <Button asChild variant="outline" size="sm" className="min-h-11">
                <a href="/support/library/housing-vehicles-inside-service-agreement">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Read: Housing and Vehicles Inside the Service Agreement
                </a>
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="space-y-3">
                <Label className="text-sm font-medium">Who holds the title to the place?</Label>
                <RadioGroup value={holder} onValueChange={(v) => setHolder(v as HolderAnswer)} className="space-y-3">
                  {HOLDER_OPTIONS.map((opt) => (
                    <div key={opt.value} className="flex items-start gap-3 rounded-lg border p-3">
                      <RadioGroupItem value={opt.value} id={`holder-${opt.value}`} className="mt-1" />
                      <Label htmlFor={`holder-${opt.value}`} className="font-normal cursor-pointer">
                        <span className="block font-medium">{opt.label}</span>
                        <span className="block text-sm text-muted-foreground">{opt.helper}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Does your household live there?</Label>
                <RadioGroup
                  value={livesThere}
                  onValueChange={(v) => setLivesThere(v as LivesThereAnswer)}
                  className="space-y-3"
                >
                  {LIVES_THERE_OPTIONS.map((opt) => (
                    <div key={opt.value} className="flex items-start gap-3 rounded-lg border p-3">
                      <RadioGroupItem value={opt.value} id={`lives-${opt.value}`} className="mt-1" />
                      <Label htmlFor={`lives-${opt.value}`} className="font-normal cursor-pointer">
                        <span className="block font-medium">{opt.label}</span>
                        <span className="block text-sm text-muted-foreground">{opt.helper}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {config && (
                <Alert>
                  <AlertDescription className="text-sm">
                    <span className="font-medium">Your path: {CONFIG_TITLES[config]}.</span>{' '}
                    {config === 'A' &&
                      'You keep living in your home. Only the named parts are rented to the entity, and those parts carry the designation. The rest of the home is yours, and it needs no occupancy paper.'}
                    {config === 'B' &&
                      'This one is not open yet. It needs a review with the team and a licensed professional before anyone builds it. Stop here and reach out.'}
                    {config === 'C' &&
                      'The structure owns the place, so it designates it and keeps the use log. If your household lives there, the household pays fair value to the entity, and that gets its own paper.'}
                    {config === 'D' &&
                      'The lease with the outside landlord stays as it is. What you build here is the designation and the use log. If your household ever lives in part of the place, the household pays fair value for that part, with its own paper, the same as when the structure owns it.'}
                  </AlertDescription>
                </Alert>
              )}

              {blockedByConfigB && (
                <Alert className="border-amber-500/50">
                  <AlertDescription className="text-sm">
                    Please pause here and talk with the team first. This path is not open in the portal.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="address">Address of the place</Label>
                <Input id="address" value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} />
              </div>
              {(config === 'A' || configNeedsResidentialLease(data)) && (
                <div>
                  <Label htmlFor="owner">
                    {config === 'A' ? 'Name on the deed' : 'Name of the household member who signs'}
                  </Label>
                  <Input id="owner" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
                </div>
              )}
              <div>
                <Label htmlFor="entity">Exact legal name of the entity</Label>
                <Input id="entity" value={entityLegalName} onChange={(e) => setEntityLegalName(e.target.value)} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="signer">Who signs for the entity</Label>
                  <Input id="signer" value={entitySignerName} onChange={(e) => setEntitySignerName(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="capacity">In what capacity</Label>
                  <Input
                    id="capacity"
                    value={entitySignerCapacity}
                    onChange={(e) => setEntitySignerCapacity(e.target.value)}
                    placeholder="Manager, Officer, and so on"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="officer">Officer who signs for the organization</Label>
                <Input id="officer" value={officerName} onChange={(e) => setOfficerName(e.target.value)} />
              </div>
              {config === 'D' && (
                <div>
                  <Label htmlFor="landlord">Name of the outside landlord</Label>
                  <Input id="landlord" value={landlordName} onChange={(e) => setLandlordName(e.target.value)} />
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <div>
                <Label htmlFor="cadence">How often is the place used for gatherings?</Label>
                <Input
                  id="cadence"
                  value={gatheringCadence}
                  onChange={(e) => setGatheringCadence(e.target.value)}
                  placeholder="Weekly on Sunday evenings, and monthly for the board sitting"
                />
              </div>

              {config === 'A' && (
                <>
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Which rooms are leased?</Label>
                    <p className="text-sm text-muted-foreground">
                      Name only the parts the entity actually uses. The rest of the home stays yours.
                    </p>
                    {portions.map((p) => (
                      <div key={p.id} className="rounded-lg border p-3 space-y-3">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Input
                            value={p.name}
                            placeholder="Room or area"
                            onChange={(e) =>
                              setPortions((prev) =>
                                prev.map((x) => (x.id === p.id ? { ...x, name: e.target.value } : x)),
                              )
                            }
                          />
                          <Input
                            value={p.square_feet}
                            placeholder="Square feet"
                            onChange={(e) =>
                              setPortions((prev) =>
                                prev.map((x) => (x.id === p.id ? { ...x, square_feet: e.target.value } : x)),
                              )
                            }
                          />
                        </div>
                        <Textarea
                          value={p.use}
                          placeholder="What happens in this room"
                          onChange={(e) =>
                            setPortions((prev) => prev.map((x) => (x.id === p.id ? { ...x, use: e.target.value } : x)))
                          }
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="min-h-11"
                          onClick={() => setPortions((prev) => prev.filter((x) => x.id !== p.id))}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="min-h-11"
                      onClick={() => setPortions((prev) => [...prev, { id: rid('p'), name: '', square_feet: '', use: '' }])}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add a room
                    </Button>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Shared areas and when they are used</Label>
                    {commonUses.map((c) => (
                      <div key={c.id} className="grid gap-3 sm:grid-cols-2 rounded-lg border p-3">
                        <Input
                          value={c.area}
                          placeholder="Shared area"
                          onChange={(e) =>
                            setCommonUses((prev) =>
                              prev.map((x) => (x.id === c.id ? { ...x, area: e.target.value } : x)),
                            )
                          }
                        />
                        <Input
                          value={c.schedule}
                          placeholder="When it is used"
                          onChange={(e) =>
                            setCommonUses((prev) =>
                              prev.map((x) => (x.id === c.id ? { ...x, schedule: e.target.value } : x)),
                            )
                          }
                        />
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="min-h-11"
                      onClick={() => setCommonUses((prev) => [...prev, { id: rid('c'), area: '', schedule: '' }])}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add a shared area
                    </Button>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="start">Start date</Label>
                      <Input id="start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="term">Term in months</Label>
                      <Input id="term" value={termMonths} onChange={(e) => setTermMonths(e.target.value)} />
                    </div>
                  </div>
                </>
              )}

              {configNeedsResidentialLease(data) && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="respart">The part your household lives in</Label>
                    <Textarea
                      id="respart"
                      value={residentialPortion}
                      onChange={(e) => setResidentialPortion(e.target.value)}
                      placeholder="Describe it plainly, room by room or floor by floor."
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="start2">Start date</Label>
                      <Input id="start2" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="term2">Term in months</Label>
                      <Input id="term2" value={termMonths} onChange={(e) => setTermMonths(e.target.value)} />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="notes">Anything else the designation should say</Label>
                <Textarea id="notes" value={designationNotes} onChange={(e) => setDesignationNotes(e.target.value)} />
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6">
              {config === 'A' && (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="rent">Monthly rent for the leased portion</Label>
                      <Input id="rent" value={portionRent} onChange={(e) => setPortionRent(e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="frvwhole">Fair rental value of the whole property</Label>
                      <Input id="frvwhole" value={frvWhole} onChange={(e) => setFrvWhole(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="frvbasis">Where the number came from</Label>
                    <Textarea id="frvbasis" value={frvBasis} onChange={(e) => setFrvBasis(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="frvsources">Listings, appraisal, or written opinion you are attaching</Label>
                    <Textarea id="frvsources" value={frvSources} onChange={(e) => setFrvSources(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="frvmethod">How you figured the portion</Label>
                    <Textarea
                      id="frvmethod"
                      value={frvPortionMethod}
                      onChange={(e) => setFrvPortionMethod(e.target.value)}
                      placeholder="Square feet, hours of use, or both. Show the math."
                    />
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Utilities and costs</Label>
                    <RadioGroup value={costMode} onValueChange={(v) => setCostMode(v as CostMode)} className="space-y-3">
                      {COST_MODE_OPTIONS.map((opt) => (
                        <div key={opt.value} className="flex items-start gap-3 rounded-lg border p-3">
                          <RadioGroupItem value={opt.value} id={`cost-${opt.value}`} className="mt-1" />
                          <Label htmlFor={`cost-${opt.value}`} className="font-normal cursor-pointer">
                            <span className="block font-medium">{opt.label}</span>
                            <span className="block text-sm text-muted-foreground">{opt.helper}</span>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    {costMode === 'itemized' && (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Anything the entity pays is paid in the entity's own name, or split by a written method.
                        </p>
                        {utilities.map((u) => (
                          <div key={u.id} className="grid gap-3 sm:grid-cols-3 rounded-lg border p-3">
                            <Input
                              value={u.item}
                              placeholder="Cost"
                              onChange={(e) =>
                                setUtilities((prev) =>
                                  prev.map((x) => (x.id === u.id ? { ...x, item: e.target.value } : x)),
                                )
                              }
                            />
                            <Input
                              value={u.who_pays}
                              placeholder="Who pays it"
                              onChange={(e) =>
                                setUtilities((prev) =>
                                  prev.map((x) => (x.id === u.id ? { ...x, who_pays: e.target.value } : x)),
                                )
                              }
                            />
                            <Input
                              value={u.method}
                              placeholder="In whose name, or how it is split"
                              onChange={(e) =>
                                setUtilities((prev) =>
                                  prev.map((x) => (x.id === u.id ? { ...x, method: e.target.value } : x)),
                                )
                              }
                            />
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-h-11"
                          onClick={() =>
                            setUtilities((prev) => [...prev, { id: rid('u'), item: '', who_pays: '', method: '' }])
                          }
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add a cost
                        </Button>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="resp">What the entity takes care of</Label>
                    <Textarea
                      id="resp"
                      value={entityResponsibilities}
                      onChange={(e) => setEntityResponsibilities(e.target.value)}
                    />
                  </div>

                  <div className="rounded-lg border p-3 space-y-3">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="dop"
                        checked={directionOfPayment}
                        onCheckedChange={(v) => setDirectionOfPayment(!!v)}
                        className="mt-1"
                      />
                      <Label htmlFor="dop" className="font-normal cursor-pointer">
                        <span className="block font-medium">Send the rent straight to my lender</span>
                        <span className="block text-sm text-muted-foreground">{DIRECTION_OF_PAYMENT_TEACHING}</span>
                      </Label>
                    </div>
                    {directionOfPayment && (
                      <div>
                        <Label htmlFor="lender">Lender the rent is sent to</Label>
                        <Input id="lender" value={lenderName} onChange={(e) => setLenderName(e.target.value)} />
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Is there a mortgage on the place?</Label>
                    <RadioGroup
                      value={hasMortgage ? 'yes' : 'no'}
                      onValueChange={(v) => setHasMortgage(v === 'yes')}
                      className="space-y-3"
                    >
                      <div className="flex items-start gap-3 rounded-lg border p-3">
                        <RadioGroupItem value="yes" id="mort-yes" className="mt-1" />
                        <Label htmlFor="mort-yes" className="font-normal cursor-pointer">
                          Yes, there is a loan on it
                        </Label>
                      </div>
                      <div className="flex items-start gap-3 rounded-lg border p-3">
                        <RadioGroupItem value="no" id="mort-no" className="mt-1" />
                        <Label htmlFor="mort-no" className="font-normal cursor-pointer">
                          No, it is owned free and clear
                        </Label>
                      </div>
                    </RadioGroup>
                    {hasMortgage && (
                      <>
                        <div>
                          <Label htmlFor="mlender">Your mortgage lender</Label>
                          <Input
                            id="mlender"
                            value={mortgageLender}
                            onChange={(e) => setMortgageLender(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="mnotes">What the loan papers say about renting out part of the home</Label>
                          <Textarea
                            id="mnotes"
                            value={mortgageNotes}
                            onChange={(e) => setMortgageNotes(e.target.value)}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}

              {configNeedsResidentialLease(data) && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="resrent">Monthly rent the household pays</Label>
                    <Input id="resrent" value={residentialRent} onChange={(e) => setResidentialRent(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="frvbasis2">Where that number came from</Label>
                    <Textarea id="frvbasis2" value={frvBasis} onChange={(e) => setFrvBasis(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="sst">Read by a stranger, this arrangement would look like</Label>
                    <Textarea
                      id="sst"
                      value={selfServingTest}
                      onChange={(e) => setSelfServingTest(e.target.value)}
                      placeholder="Finish the sentence in your own words."
                    />
                  </div>
                </div>
              )}

              {config === 'D' && (
                <Alert>
                  <AlertDescription className="text-sm">
                    Your lease with the landlord stays exactly as it is. Nothing here changes it. All you need is the
                    designation and the use log. If your household ever lives in part of the place, the household pays fair value for that part, with its own paper, the same as when the structure owns it.
                  </AlertDescription>
                </Alert>
              )}

              {config === 'C' && !configNeedsResidentialLease(data) && (
                <Alert>
                  <AlertDescription className="text-sm">
                    Nobody lives there, so there is no occupancy paper to build. All you need is the designation and the
                    use log.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step === 6 && (
            <div className="space-y-4 text-sm">
              <p className="font-medium">{config ? CONFIG_TITLES[config] : 'Answer the two questions first.'}</p>
              <p>Place: {propertyAddress || 'not set yet'}</p>
              <p>Gatherings: {gatheringCadence || 'not set yet'}</p>
              {config === 'A' && (
                <>
                  <p>Rooms leased: {portions.filter((p) => p.name.trim()).length}</p>
                  <p>Rent for the leased portion: {portionRent || 'not set yet'}</p>
                  <p>Rent sent to the lender: {directionOfPayment ? 'yes' : 'no'}</p>
                </>
              )}
              {configNeedsResidentialLease(data) && <p>Household rent: {residentialRent || 'not set yet'}</p>}
              <Alert>
                <AlertDescription className="text-sm">{SEQUENCING_TEACHING}</AlertDescription>
              </Alert>
              <p className="font-medium">
                After setup, this whole arrangement asks two things of you: one tap a month for the log, and one sitting
                a year. That is the design.
              </p>
              <Button onClick={finish} className="min-h-11">
                Save and go to the papers
              </Button>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep((s) => s - 1)} className="min-h-11">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            {step < 6 && (
              <Button onClick={goNext} disabled={blockedByConfigB && step === 2} className="min-h-11">
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default HousingPhase1;
